import type { ResultBand } from './types';

/**
 * Presentation helpers only.
 *
 * §13 — no headline metric is computed here. Every percentage, band and label
 * arrives from the API already decided; this file formats what it is given. The one
 * thing that would break that rule is deriving a band from a percentage in the
 * browser, so there is deliberately no such function.
 */

export function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const date = new Date(iso.length <= 10 ? `${iso}T00:00:00Z` : iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: iso.length <= 10 ? 'UTC' : undefined,
  });
}

export function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatPct(value: number | null | undefined): string {
  return value === null || value === undefined ? '—' : `${Math.round(value)}%`;
}

/** R3 — the score reads `n/m`, where m is the applicable count, not always 10. */
export function formatScore(score: number, applicable: number): string {
  return applicable === 0 ? '—' : `${score}/${applicable}`;
}

/** Signed delta for the change chip, e.g. `+8` / `−3`. */
export function formatDelta(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  const rounded = Math.round(value);
  if (rounded === 0) return 'no change';
  // U+2212 minus, not a hyphen — it aligns with digits.
  return rounded > 0 ? `+${rounded}` : `−${Math.abs(rounded)}`;
}

export const BAND_LABEL: Record<ResultBand, string> = {
  IN_CONTROL_LEANING: 'In-Control leaning',
  MIXED: 'Mixed',
  IN_CHARGE_LEANING: 'In-Charge leaning',
};

/** Tailwind classes per band. Amber is reserved for the In-Charge end of the scale. */
export const BAND_CLASS: Record<ResultBand, string> = {
  IN_CONTROL_LEANING: 'border-[#e0796b]/35 bg-[#e0796b]/12 text-[#e0796b]',
  MIXED: 'border-secondary/35 bg-secondary-soft text-secondary',
  IN_CHARGE_LEANING: 'border-accent/35 bg-accent-soft text-accent',
};

export const CLASSIFICATION_LABEL = {
  IN_CHARGE: 'In-Charge',
  IN_CONTROL: 'In-Control',
  NOT_APPLICABLE: 'Not applicable',
} as const;

export const CLASSIFICATION_CLASS = {
  IN_CHARGE: 'border-accent/35 bg-accent-soft text-accent',
  IN_CONTROL: 'border-[#e0796b]/35 bg-[#e0796b]/12 text-[#e0796b]',
  NOT_APPLICABLE: 'border-line bg-surface-raised text-muted',
} as const;

/** Turns A/B/C/D into the label shown on an option card. */
export function optionLetter(position: number): string {
  return String.fromCharCode(64 + position);
}

export function pluralise(count: number, singular: string, plural = `${singular}s`): string {
  return `${count} ${count === 1 ? singular : plural}`;
}
